import React, { useState } from 'react';
import { paths } from 'config/path_helpers';
import Accordion from '@magoosh/lib/accordion';
import StatusMessage from '../../../../lib/status_message';
import StudyItemCard from '../../../../organisms/study_item_card';
import ConditionalWrapper from '../../../../utils/conditional_wrapper';
import CheckpointCard from '../../../../organisms/checkpoint_card';
import { DefaultButton, LinkButton } from '@magoosh/lib/button';
import AddTopicFormModal from '@magoosh/gre/adaptive_prep_v1/organisms/add_topic_form_modal';
import RefreshStudyPlanFormModal from '@magoosh/gre/adaptive_prep_v1/organisms/refresh_study_plan_form_modal';
import style from '../../style.module.scss';
import { Collapse, Modal } from 'react-bootstrap';
import { checkpointProps } from './data';
import MixedPracticeForm from '@magoosh/gre/adaptive_prep_v1/organisms/mixed_practice_form';
import { useUserContext } from '@magoosh/context/user_context';
import { useStudyPlanContext } from '../../context';
import PrioritizedCheckpoint from '../../checkpoints/prioritized';

export const ToDoTab: React.FC = () => {
  const {
    studyPlanState,
    loading,
    authToken,
    addATopic,
    moveToEnd,
    deferAssessment,
    dismissRefresh,
    markComplete,
    refreshPlan
  } = useStudyPlanContext();

  const {
    prioritizedCheckpoint,
    toDoItems,
    currentAndUpcomingCheckpoints,
    testDate
  } = studyPlanState;

  const { adaptivePrepData } = useUserContext();

  const [displayRefreshModal, setDisplayRefreshModal] = useState(false);
  const [displayAddTopicModal, setDisplayAddTopicModal] = useState(false);
  const [expandedCards, setExpandedCards] = useState(false);

  const mixedPracticeModal = useMixedPracticeModal();

  return (
    <>
      {loading ? (
        <StatusMessage headline="How exciting!" text="Your study plan is almost ready..." isColored={true} />
      ) : (
        <>
          {prioritizedCheckpoint && (
            <PrioritizedCheckpoint
              deferAssessment={deferAssessment}
              dismissRefresh={dismissRefresh}
              prioritizedCheckpoint={prioritizedCheckpoint}
              refreshPlan={refreshPlan}
              testDate={testDate}
            />
          )}
          <div>
            {toDoItems.slice(0, 10).map((item, index) => (
              <StudyItemCard
                key={index}
                section={item.section}
                variant={item.type === 'Segment' ? 'secondary' : 'info'}
                headline={item.headline}
                subheadline={item.subheadline}
                significanceLevel={item.significanceLevel}
                minutes={item.estimatedTimeMinutes}
                id={item.id}
                primaryCTA={{
                  ...item.primaryCta,
                  variant: index === 0 && !prioritizedCheckpoint ? 'secondary' : 'default',
                  onClick: item.type === 'Practice' ? () => mixedPracticeModal.open(item.id) : undefined
                }}
                secondaryCTA={{
                  text: item.type === 'Segment' ? 'Move to End' : 'Mark Completed',
                  onClick: () => {
                    item.type === 'Segment' ? moveToEnd(item) : markComplete(item);
                  }
                }}
                isLarger={index === 0 && !prioritizedCheckpoint}
                description={item.description}
                shortDescription={item.shortDescription}
                locked={item.locked}
              />
            ))}
          </div>

          <Collapse in={expandedCards}>
            <div>
              {toDoItems.slice(10).map((item, index) => (
                <StudyItemCard
                  key={index}
                  section={item.section}
                  variant={item.type === 'Segment' ? 'secondary' : 'info'}
                  headline={item.headline}
                  subheadline={item.subheadline}
                  significanceLevel={item.significanceLevel}
                  minutes={item.estimatedTimeMinutes}
                  id={item.id}
                  primaryCTA={{
                    ...item.primaryCta,
                    variant: 'default',
                    onClick: item.type === 'Practice' ? () => mixedPracticeModal.open(item.id) : undefined
                  }}
                  secondaryCTA={{
                    text: item.type === 'Segment' ? 'Move to End' : 'Mark Completed',
                    onClick: () => {
                      item.type === 'Segment' ? moveToEnd(item) : markComplete(item);
                    }
                  }}
                  description={item.description}
                  shortDescription={item.shortDescription}
                  locked={item.locked}
                />
              ))}
            </div>
          </Collapse>
          {!expandedCards && toDoItems.length >= 9 && (
            <div className="text-xs-center">
              <DefaultButton
                onClick={() => {
                  setExpandedCards(!expandedCards);
                }}>
                Show All
              </DefaultButton>
            </div>
          )}
          {adaptivePrepData.hasStudyPlan && (expandedCards || toDoItems.length < 9) && (
            <>
              <div className="text-center">
                <DefaultButton onClick={() => setDisplayRefreshModal(true)}>
                  Check for Recommendations
                </DefaultButton>
              </div>
              <div className="text-center u-margin-T-s">
                <LinkButton className="text-body" onClick={() => setDisplayAddTopicModal(true)}>
                  Add a Specific Topic
                </LinkButton>
              </div>
            </>
          )}

          {adaptivePrepData.hasStudyPlan && (
            <>
              <hr className={style.sectionDivider} />
              <h3>
                <strong>Upcoming Checkpoints</strong>
              </h3>
              <p className="u-margin-B-l">
                Checkpoints are spaced out to help prepare you for the actual test format and keep your study
                plan relevant. Some checkpoints also offer estimated scores so you can track your progress.
                When your next checkpoint is available it will be featured at the top of your study plan.
              </p>
              {currentAndUpcomingCheckpoints.map((checkpoint, idx) => {
                return (
                  // We need the form so we can hit the right endpoint for not-started assessments
                  <ConditionalWrapper
                    key={idx}
                    condition={checkpoint.type === 'Assessment'}
                    wrapper={(children) => (
                      <form action={paths.practiceTests()} method="POST">
                        <input
                          type="hidden"
                          name="mock_test[template_id]"
                          value={checkpoint.mockTestTemplateId}
                        />
                        <input type="hidden" name="authenticity_token" value={authToken} />
                        {children}
                      </form>
                    )}>
                    <CheckpointCard {...checkpointProps(checkpoint, testDate)} />
                  </ConditionalWrapper>
                );
              })}

              <hr className={style.sectionDivider} />
              <div>
                <h3>
                  <strong>How Your Adaptive Study Plan Works</strong>
                </h3>
                <Accordion title="Why am I seeing topics repeat over time?" panelClassName={style.faqPanel}>
                  Research shows that spaced repetition of topics you’ve already studied is critical to
                  retention. Your Adaptive Study Plan provides optimally spaced review sessions and
                  strategically timed vocabulary practice so you have the greatest opportunity to improve your
                  score.
                </Accordion>
                <Accordion title="What should I study first?" panelClassName={style.faqPanel}>
                  We’ll suggest what to do next at the top of the list. Your study plan prioritizes topics
                  that are most significant to your GRE score, and will adapt to how you’ve performed on mixed
                  review and score predictors like assessments and practice tests. You have the flexibility to
                  work your way down the list in any order, or move activities down to come back to them
                  later.
                </Accordion>
                <Accordion
                  title="I'm looking for a specific topic and I don’t see it. Can I add it to my study plan?"
                  panelClassName={style.faqPanel}>
                  Yes! If you’d like to include a specific topic in your study plan now, you may add a topic
                  here. If you don’t see a specific topic, it’s likely because it isn't as important to your
                  score as other topics. As you complete your current study plan, you'll get new priority
                  topics to study.
                </Accordion>
                <Accordion
                  title="How long should I keep studying for the GRE?"
                  panelClassName={style.faqPanel}>
                  We’re here to give you the best shot at achieving your target score on the GRE. That being
                  said, we recommend studying and reviewing up until those final days before test day. Your
                  study plan will bring back topics you’ve already covered if your performance on those topics
                  starts to weaken. And your mixed practice will pull questions from all the topics you’ve
                  studied to help keep those fresh. We’ve got your back, and you will get through this! 💪
                </Accordion>
              </div>

              <RefreshStudyPlanFormModal
                isOpen={displayRefreshModal}
                closeModal={() => {
                  setDisplayRefreshModal(false);
                }}
                refresh={refreshPlan}
              />
              <AddTopicFormModal
                isOpen={displayAddTopicModal}
                closeModal={() => {
                  setDisplayAddTopicModal(false);
                }}
                onSubmit={addATopic}
              />
              <Modal show={mixedPracticeModal.isOpen} onHide={mixedPracticeModal.close}>
                <MixedPracticeForm studyPlanItemId={mixedPracticeModal.studyPlanItemId} />
              </Modal>
            </>
          )}
        </>
      )}
    </>
  );
};

export default ToDoTab;

function useMixedPracticeModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [studyPlanItemId, setStudyPlanItemId] = useState<number>(null);

  return {
    open: (studyPlanItemId: number) => {
      setIsOpen(true);
      setStudyPlanItemId(studyPlanItemId);
    },
    close: () => {
      setIsOpen(false);
    },
    isOpen,
    studyPlanItemId
  };
}
